import React, { createContext, useContext, useState, useEffect } from 'react';
import { Maximize, Minimize } from 'lucide-react';
import { Button } from '../components/ui/button';

const PresentationContext = createContext();

export const usePresentationMode = () => {
  const context = useContext(PresentationContext);
  if (!context) {
    throw new Error('usePresentationMode must be used within PresentationProvider');
  }
  return context;
};

export const PresentationProvider = ({ children }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [laserPointer, setLaserPointer] = useState({ x: 0, y: 0, active: false });

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  // Laser pointer avec touche L
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'l' || e.key === 'L') {
        setLaserPointer(prev => ({ ...prev, active: !prev.active }));
      }
      if (e.key === 'Escape') {
        setLaserPointer(prev => ({ ...prev, active: false }));
      }
    };

    const handleMouseMove = (e) => {
      if (laserPointer.active) {
        setLaserPointer(prev => ({ ...prev, x: e.clientX, y: e.clientY }));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [laserPointer.active]);

  return (
    <PresentationContext.Provider value={{ isFullscreen, toggleFullscreen, showNotes, setShowNotes, laserPointer }}>
      {children}
      
      {/* Fullscreen toggle button */}
      <Button
        onClick={toggleFullscreen}
        className="hidden md:flex fixed bottom-20 right-8 z-50 bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 border-2 border-orange-500/50 shadow-lg shadow-orange-500/20 items-center justify-center w-12 h-12 rounded-lg transition-all hover:scale-110"
        aria-label={isFullscreen ? "Quitter le plein écran" : "Activer le plein écran"}
      >
        {isFullscreen ? <Minimize className="h-6 w-6" /> : <Maximize className="h-6 w-6" />}
      </Button>

      {/* Laser pointer */}
      {laserPointer.active && (
        <div
          className="fixed pointer-events-none z-[9999]"
          style={{
            left: laserPointer.x,
            top: laserPointer.y,
            width: '20px',
            height: '20px',
            transform: 'translate(-50%, -50%)'
          }}
        >
          <div className="w-full h-full bg-red-500 rounded-full opacity-80 animate-pulse" />
          <div className="absolute inset-0 w-full h-full bg-red-500 rounded-full blur-md opacity-60" />
        </div>
      )}
      
      {/* Laser indicator */}
      {laserPointer.active && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-red-500/90 text-white px-4 py-2 rounded-lg shadow-lg">
          Pointeur laser actif (Appuyez sur L ou ESC pour désactiver)
        </div>
      )}
    </PresentationContext.Provider>
  );
};
